import React, { useMemo } from 'react';
import { Table, Tag, Typography, Space, message } from 'antd';
import dayjs from 'dayjs';
import PageList from '../../components/Templates/PageList';
import { overdueApi, RepaymentLedgerJoined, InstallmentOrderJoinedListRequest } from '../../services/statistics/overdue';
import { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder/types';
import { useShopMap, useMainBodyMap } from '../../services/store/organization.store';
import { mainBodyStoreUnionMultiSelect } from '../../components/Business/QueryFieldPresets';
import { ORDER_STATUS_LABEL_MAP, OrderStatus } from '../../types/process/order-data';
import { OrderStatusMap, OrderStatus as MysqlOrderStatus } from '../../types/process/order';

const { Text } = Typography;

const BILL_STATUS_FINISHED = [
  "支付完成",
  "支付完成(等待)",
  // "提前完结",
  // "已退款",
  // "废弃",
  // "已取消",
];

const OrderBillRemindList: React.FC = () => {
  const { shopList } = useShopMap();
  const { mainBodyList } = useMainBodyMap();
  const resolveDateQuery = (d: number) => {
    const today = dayjs().startOf('day');
    const targetDate = today.add(d, 'day');
    return {
       operator: 'between',
       value: [targetDate.format('YYYY-MM-DD 00:00:00'), targetDate.format('YYYY-MM-DD 23:59:59')]
    };
  };

  const queryFields: FieldConfig[] = [
    {
      key: 'orderNo',
      group: 'order',
      label: '订单号',
      type: 'string',
      operators: ['like'],
    },
    {
      ...mainBodyStoreUnionMultiSelect(mainBodyList, shopList, 'storeId', 'order'),
    },
    {
      key: 'purchaseChannel',
      group: 'order',
      label: '采购渠道',
      type: 'select',
      operators: ['eq'],
      options: [
        { label: '平台', value: '平台', queryValue: { key: 'warehouseSource', operator: 'eq', value: 3 } },
        { label: '自采', value: '自采', queryValue: { key: 'warehouseSource', operator: 'in', value: [1, 4] } },
      ]
    },
    {
      key: 'orderStatus',
      group: 'order',
      label: '人人租状态',
      type: 'select',
      operators: ['in', 'nin', 'eq', 'neq'],
      options: Object.entries(ORDER_STATUS_LABEL_MAP).map(([k, v]) => ({ label: v, value: Number(k) })),
      width: 200
    },
    {
      key: 'mysqlOrderStatus',
      group: 'order',
      label: '工单状态',
      type: 'select',
      operators: ['in', 'nin', 'eq', 'neq'],
      options: Object.entries(OrderStatusMap).map(([k, v]) => ({ label: v, value: Number(k) })),
      width: 200
    },
    {
      key: 'userAuthName',
      group: 'order',
      label: '用户姓名',
      type: 'string',
      operators: ['like'],
    },
    {
      key: 'userPhone',
      group: 'order',
      label: '手机号',
      type: 'string',
      operators: ['like'],
    },
    {
      key: 'expectedDeductionDate',
      group: 'installments',
      label: '距离账单日',
      type: 'number',
      operators: ['eq'],
    },
    {
      key: 'paymentStatus',
      group: 'installments',
      label: '支付状态',
      type: 'select',
      operators: ['eq'],
      getOptions: () => [
        { label: '未支付', value: 'UNPAID', queryValue: { operator: 'nin', value: BILL_STATUS_FINISHED } },
        { label: '已支付', value: 'PAID', queryValue: { operator: 'in', value: BILL_STATUS_FINISHED } },
      ],
    }
  ];

  const defaultQuery: ForgeQuery<any> = useMemo(() => {
    return {
      filter: {
        'installments': {
          'expectedDeductionDate': 3,
          'paymentStatus': '未支付'
        },
        'order': {
          'orderStatus': { operator: 'nin', value: [OrderStatus.Complete, OrderStatus.CloseUser, OrderStatus.CloseServer, OrderStatus.CloseSystem, OrderStatus.HadClosedOrder] },
          'mysqlOrderStatus': { operator: 'nin', value: [MysqlOrderStatus.Cancelled, MysqlOrderStatus.Inbound] }
        }
      },
      sort: [{ field: 'installmentDetail.expectedDeductionDate', order: 'asc' }]
    };
  }, []);

  const columns = [
    {
      title: '订单号',
      key: 'orderNo',
      render: (_: any, record: RepaymentLedgerJoined) => (
         <Text copyable>{record.orderData?.orderNo || record.installmentDetail.orderNo}</Text>
      ),
    },
    {
      title: '店铺',
      key: 'storeName',
      render: (_: any, record: RepaymentLedgerJoined) => {
        const store = shopList.find(s => s.code === String(record.orderData?.storeId));
        return <Text>{store ? store.name : (record.orderData?.storeId || '-')}</Text>;
      },
    },
    {
      title: '采购渠道',
      key: 'purchaseChannel',
      render: (_: any, record: RepaymentLedgerJoined) => {
        const source = record.orderData?.warehouseSource;
        if (source === 1 || source === 4) return <Text>自采</Text>;
        if (source === 3) return <Text>平台</Text>;
        return <Text>-</Text>;
      },
    },
    {
      title: '下单时间',
      key: 'orderCreatedAt',
      render: (_: any, record: RepaymentLedgerJoined) => (
         <Text type="secondary">
           {record.orderData?.orderCreatedAt ? dayjs(record.orderData.orderCreatedAt).format('YYYY-MM-DD HH:mm') : '-'}
         </Text>
      ),
    },
    {
      title: '工单状态',
      key: 'mysqlOrderStatus',
      render: (_: any, record: RepaymentLedgerJoined) => (
         <Text>{record.order?.orderStatus !== undefined ? OrderStatusMap[record.order.orderStatus] || '-' : '-'}</Text>
      ),
    },
    {
      title: '人人租状态',
      key: 'orderStatus',
      render: (_: any, record: RepaymentLedgerJoined) => (
         <Text>{record.orderData?.orderStatus !== undefined ? ORDER_STATUS_LABEL_MAP[record.orderData.orderStatus] || '-' : '-'}</Text>
      ),
    },
    {
      title: '用户姓名',
      key: 'userAuthName',
      render: (_: any, record: RepaymentLedgerJoined) => (
         <Text>{record.orderData?.userAuthName || '-'}</Text>
      ),
    },
    {
      title: '手机号',
      key: 'userPhone',
      render: (_: any, record: RepaymentLedgerJoined) => (
         <Text type="secondary">{record.orderData?.userPhone || '-'}</Text>
      ),
    },
    {
      title: '期数',
      key: 'period',
      render: (_: any, record: RepaymentLedgerJoined) => (
         <Text>{record.installmentDetail.periodNumber} / {record.installmentDetail.totalPeriods}</Text>
      ),
    },
    {
      title: '账单金额',
      key: 'billAmount',
      render: (_: any, record: RepaymentLedgerJoined) => (
         <Text>¥{record.installmentDetail.billAmount}</Text>
      ),
    },
    {
      title: '账单日',
      key: 'expectedDeductionDate',
      render: (_: any, record: RepaymentLedgerJoined) => (
         <Text type="secondary">
           {record.installmentDetail.expectedDeductionDate ? dayjs(record.installmentDetail.expectedDeductionDate).format('YYYY-MM-DD') : '-'}
         </Text>
      ),
    },
    {
      title: '支付状态',
      key: 'isPaid',
      render: (_: any, record: RepaymentLedgerJoined) => {
         const isPaid = !!record.installmentDetail.actualPaymentDate;
         return (
            <Tag color={isPaid ? 'green' : 'orange'}>
              {isPaid ? '已支付' : '未支付'}
            </Tag>
         );
      },
    },
    {
      title: '距离账单日',
      key: 'diffDays',
      render: (_: any, record: RepaymentLedgerJoined) => {
          const expectedDate = record.installmentDetail.expectedDeductionDate;
          const today = dayjs().startOf('day');
          const billDate = dayjs(expectedDate).startOf('day');
          const diffDays = billDate.diff(today, 'day');
          return <Tag color="blue">{diffDays}天</Tag>;
      },
    },
    // {
    //   title: '状态明细',
    //   key: 'paymentStatusDetail',
    //   render: (_: any, record: RepaymentLedgerJoined) => (
    //     record.installmentDetail.paymentStatus ? 
    //     <Text type="secondary" style={{ fontSize: 12 }}>{record.installmentDetail.paymentStatus}</Text> : '-'
    //   ),
    // }
  ];

  const handleExport = async (query: ForgeQuery) => {
    try {
      const queryParams = JSON.parse(JSON.stringify(query));
      
      let dateVal = queryParams.filter?.installments?.expectedDeductionDate;
      if (dateVal !== undefined) {
         const d = typeof dateVal === 'object' ? dateVal.value : dateVal;
         if (typeof d === 'number') {
             queryParams.filter.installments.expectedDeductionDate = resolveDateQuery(d);
         }
      }

      if (queryParams.filter?.installments?.paymentStatus) {
         const statusVal = typeof queryParams.filter.installments.paymentStatus === 'object' ? queryParams.filter.installments.paymentStatus.value : queryParams.filter.installments.paymentStatus;
         if (statusVal === 'UNPAID') {
            queryParams.filter.installments.paymentStatus = { operator: 'nin', value: BILL_STATUS_FINISHED };
         } else if (statusVal === 'PAID') {
            queryParams.filter.installments.paymentStatus = { operator: 'in', value: BILL_STATUS_FINISHED };
         }
      }

      await overdueApi.exportBillRemindList(queryParams as unknown as InstallmentOrderJoinedListRequest);
      message.success('导出任务已提交，请前往我的导出查看导出列表');
    } catch (e: any) {
      const errorMsg = e.response?.data?.message || e.message || '导出失败';
      message.error(errorMsg);
    }
  };

  return (
    <PageList<RepaymentLedgerJoined>
      queryFields={queryFields}
      defaultQuery={defaultQuery}
      layoutConfig={{ pagination: 'fixed' }}
      onExport={handleExport}
      fetcher={async (params) => {
        // Deep clone params to avoid mutating PageList state
        const queryParams = JSON.parse(JSON.stringify(params));
        
        let dateVal = queryParams.filter?.installments?.expectedDeductionDate;
        if (dateVal !== undefined) {
           const d = typeof dateVal === 'object' ? dateVal.value : dateVal;
           if (typeof d === 'number') {
               queryParams.filter.installments.expectedDeductionDate = resolveDateQuery(d);
           }
        }

        if (queryParams.filter?.installments?.paymentStatus) {
           const statusVal = typeof queryParams.filter.installments.paymentStatus === 'object' ? queryParams.filter.installments.paymentStatus.value : queryParams.filter.installments.paymentStatus;
           if (statusVal === 'UNPAID') {
              queryParams.filter.installments.paymentStatus = { operator: 'nin', value: BILL_STATUS_FINISHED };
           } else if (statusVal === 'PAID') {
              queryParams.filter.installments.paymentStatus = { operator: 'in', value: BILL_STATUS_FINISHED };
           }
        }
        if (queryParams.filter?.order?.mysqlOrderStatus) {
           const statusVal = typeof queryParams.filter.order.mysqlOrderStatus === 'object' ? queryParams.filter.order.mysqlOrderStatus.value : queryParams.filter.order.mysqlOrderStatus;
           queryParams.filter.order.mysqlOrderStatus = { operator: queryParams.filter.order.mysqlOrderStatus.operator || 'eq', value: statusVal };
        }

        const res = await overdueApi.getBillRemindList(queryParams as unknown as InstallmentOrderJoinedListRequest);
        return {
          rows: res.data.items,
          pagination: res.data.pagination,
        };
      }}
      renderList={({ rows, loading }) => (
        <Table 
           dataSource={rows} 
           columns={columns} 
           rowKey={(r) => r.installmentDetail.id}
           loading={loading}
           pagination={false}
           scroll={{ y: 'calc(100vh - 240px)' }}
           size="middle"
        />
      )}
    />
  );
};

export default OrderBillRemindList;
